import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

import { OnboardingContext, getNextActiveStepIndex, tourSteps } from './dashboard-tour-steps';

import type { EntitySummary, NarrativeSummary } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export function DashboardTour() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { selectedNovelId } = useNovel();
  const { getSummaries } = useNovelRepository();
  const { userData, updateProfile, isLoading: isProfileLoading } = useUserProfile();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [domReady, setDomReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const chapters = getSummaries<NarrativeSummary>('chapters');
  const characters = getSummaries<EntitySummary>('characters');
  const locations = getSummaries<EntitySummary>('locations');
  const scenes = getSummaries<NarrativeSummary>('scenes');

  const search = location.search as Record<string, unknown>;
  const hasTextId = typeof search.textId === 'string' && !!search.textId;

  const ctx: OnboardingContext = useMemo(() => ({
    isDesktop,
    selectedNovelId,
    hasChapters: chapters.length > 0,
    firstChapterId: chapters[0]?.id,
    characterCount: characters.length,
    locationCount: locations.length,
    sceneCount: scenes.length,
    firstSceneTextId: scenes[0]?.mainContentId,
    hasTextId
  }), [isDesktop, selectedNovelId, chapters, characters, locations, scenes, hasTextId]);

  useEffect(() => {
    if (isProfileLoading) return;
    const completed = localStorage.getItem('eplotera_onboarding_completed') === 'true';
    setRun(userData.hasSeenDashboardTour !== true && !completed);
    setIsInitializing(false);

    const stored = localStorage.getItem('eplotera_onboarding_step_index');
    if (stored) setStepIndex(parseInt(stored, 10));
  }, [userData, isProfileLoading]);

  // Handle conditional step skipped/shown logic
  useEffect(() => {
    const currentStepDef = tourSteps[stepIndex];
    if (currentStepDef && currentStepDef.isConditional && !currentStepDef.isConditional(ctx)) {
      const activeNextIndex = getNextActiveStepIndex(stepIndex + 1, 'forward', ctx);
      if (activeNextIndex < tourSteps.length) {
        setStepIndex(activeNextIndex);
        localStorage.setItem('eplotera_onboarding_step_index', String(activeNextIndex));
      } else {
        setRun(false);
      }
    }
  }, [stepIndex, ctx]);

  // Handle checking for target element's presence in the DOM
  useEffect(() => {
    const currentStepDef = tourSteps[stepIndex];
    if (!currentStepDef) {
      setDomReady(false);
      return;
    }

    let attempts = 0;

    const checkDom = () => {
      const target = typeof currentStepDef.target === 'function'
        ? currentStepDef.target(ctx)
        : currentStepDef.target;
      const el = document.querySelector(target);
      const isConditionMet = !currentStepDef.waitForCondition || currentStepDef.waitForCondition(ctx);

      if (el && currentStepDef.path === pathname && isConditionMet) {
        setDomReady(true);
        clearInterval(checkInterval);
      } else {
        setDomReady(false);
        if (currentStepDef.waitForCondition && !isConditionMet) {
          // Wait indefinitely
        } else {
          attempts++;
          if (attempts > 30) clearInterval(checkInterval);
        }
      }
    };

    const checkInterval = setInterval(checkDom, 100);
    checkDom();
    return () => clearInterval(checkInterval);
  }, [stepIndex, pathname, ctx]);

  const steps: Step[] = tourSteps.map((step) => ({
    target: typeof step.target === 'function' ? step.target(ctx) : step.target,
    content: t(step.contentKey),
    placement: step.placement,
    disableBeacon: step.disableBeacon,
  }));

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, status, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      localStorage.setItem('eplotera_onboarding_completed', 'true');

      // Close all drawers on finish/skip
      ['#editor-outline-btn', '#editor-properties-btn', '#editor-entities-btn'].forEach(id => {
        const btn = document.querySelector(id);
        if (btn?.classList.contains('MuiIconButton-colorPrimary')) {
          (btn as HTMLElement).click();
        }
      });

      try {
        await updateProfile({ hasSeenDashboardTour: true });
      } catch (error) {
        console.error("Failed to update tour state:", error);
      }
      return;
    }

    if (type === 'step:before') {
      const currentStepDef = tourSteps[data.index];
      if (currentStepDef?.onBefore) {
        await currentStepDef.onBefore(navigate, ctx);
      }
    }

    if (type === 'step:after' || (type === 'error' && run)) {
      if (action === 'prev') {
        const prevIndex = stepIndex - 1;
        const activePrevIndex = getNextActiveStepIndex(prevIndex, 'backward', ctx);
        if (activePrevIndex >= 0) {
          const prevStepDef = tourSteps[activePrevIndex];
          if (prevStepDef.path !== pathname) navigate({ to: prevStepDef.path });
          setStepIndex(activePrevIndex);
          localStorage.setItem('eplotera_onboarding_step_index', String(activePrevIndex));
        }
        return;
      }

      const nextIndex = stepIndex + 1;
      const activeNextIndex = getNextActiveStepIndex(nextIndex, 'forward', ctx);
      if (activeNextIndex < tourSteps.length) {
        const currentStepDef = tourSteps[stepIndex];
        if (currentStepDef.onNext && action === 'next') {
          await currentStepDef.onNext(navigate, ctx);
        }
        setStepIndex(activeNextIndex);
        localStorage.setItem('eplotera_onboarding_step_index', String(activeNextIndex));
      } else {
        setRun(false);
        localStorage.setItem('eplotera_onboarding_completed', 'true');
        try {
          await updateProfile({ hasSeenDashboardTour: true });
        } catch (error) {
          console.error("Failed to update tour state:", error);
        }
      }
    }
  };

  if (isInitializing) return null;

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      run={run && domReady}
      stepIndex={stepIndex}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#a1824a',
        },
      }}
      locale={{
        back: t('tour.actions.back'),
        close: t('tour.actions.close'),
        last: t('tour.actions.finish'),
        next: t('tour.actions.next'),
        nextLabelWithProgress: t('tour.actions.next_with_progress'),
        skip: t('tour.actions.skip'),
      }}
    />
  );
}
